import { z } from 'zod';

/**
 * Config editors (docs/DESIGN.md §4): a profile describes a game's config files as labelled
 * form fields. Values travel as the raw strings the file holds; `configFieldValueSchema`
 * turns a field's declarative rules into one Zod schema that the web uses while editing and
 * the API uses again on save.
 */

/** Formats Hearth can edit field by field; the others are raw-only (Monaco) for now. */
export const CONFIG_FORM_FORMATS = ['properties', 'cfg'] as const;
export const CONFIG_FORMATS = [...CONFIG_FORM_FORMATS, 'yaml', 'json'] as const;
export type ConfigFormat = (typeof CONFIG_FORMATS)[number];

export const CONFIG_FIELD_TYPES = [
  'text',
  'number',
  'boolean',
  'select',
  'multiline',
  'secret',
  'list',
] as const;
export type ConfigFieldType = (typeof CONFIG_FIELD_TYPES)[number];

const isRegex = (p: string) => {
  try {
    new RegExp(p, 'u');
    return true;
  } catch {
    return false;
  }
};
const regex = z.string().min(1).max(500).refine(isRegex, 'Not a valid regular expression.');

/** A key as it appears in the file. */
const configKey = z
  .string()
  .min(1)
  .max(128)
  .regex(/^[^\s=:#!"]+$/, 'Keys can’t contain spaces, quotes or = : # !.');

const configFieldObject = z
  .object({
    key: configKey,
    label: z.string().trim().min(1).max(80),
    type: z.enum(CONFIG_FIELD_TYPES),
    /** Plain-English help shown under the control. */
    help: z.string().max(400).optional(),
    /** What the game uses when the key is missing; shown, never written on its own. */
    default: z.union([z.string().max(1000), z.number(), z.boolean()]).optional(),
    /** `select`: the allowed values. */
    options: z
      .array(z.object({ value: z.string().max(200), label: z.string().min(1).max(80).optional() }))
      .min(1)
      .max(100)
      .optional(),
    /** `number`: bounds, and whether fractions are allowed. */
    min: z.number().optional(),
    max: z.number().optional(),
    integer: z.boolean().optional(),
    /** text-like fields (and each `list` item): length and pattern. */
    minLength: z.number().int().min(0).max(10_000).optional(),
    maxLength: z.number().int().min(1).max(10_000).optional(),
    pattern: regex.optional(),
    /** Shown when `pattern` doesn't match. */
    patternMessage: z.string().max(200).optional(),
    /** `list`: the character between items (default ","), and how many items at most. */
    separator: z.string().min(1).max(3).optional(),
    maxItems: z.number().int().min(1).max(1000).optional(),
    /** Overrides the editor's `restartRequired` for this field. */
    restartRequired: z.boolean().optional(),
  })
  .strict();
export type ConfigField = z.infer<typeof configFieldObject>;

export const configFieldSchema = configFieldObject.superRefine((f, ctx) => {
  if (f.type === 'select' && !f.options)
    ctx.addIssue({ code: 'custom', path: ['options'], message: 'A select needs options.' });
  if (f.type !== 'select' && f.options)
    ctx.addIssue({ code: 'custom', path: ['options'], message: 'Only selects have options.' });
  if (f.min !== undefined && f.max !== undefined && f.min > f.max)
    ctx.addIssue({ code: 'custom', path: ['min'], message: 'min is greater than max.' });
  if (f.default !== undefined) {
    const check = configFieldValueSchema(f).safeParse(configDefaultRaw(f));
    if (!check.success)
      ctx.addIssue({
        code: 'custom',
        path: ['default'],
        message: `The default isn’t valid: ${check.error.issues[0]?.message}`,
      });
  }
});

export const configGroupSchema = z.object({
  label: z.string().trim().min(1).max(80),
  description: z.string().max(400).optional(),
  fields: z.array(configFieldSchema).min(1).max(100),
});
export type ConfigGroup = z.infer<typeof configGroupSchema>;

const editorId = z
  .string()
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Lowercase letters, digits and dashes.')
  .max(64);
export const configEditorIdSchema = editorId;

export const configEditorSchema = z
  .object({
    id: editorId,
    label: z.string().trim().min(1).max(80),
    description: z.string().max(400).optional(),
    /** The file, from the server's root. */
    path: z
      .string()
      .startsWith('/')
      .max(512)
      .refine((p) => !p.split('/').includes('..'), 'No ".." segments.'),
    format: z.enum(CONFIG_FORMATS),
    /** Fields shown in the form; empty for raw-only formats. */
    groups: z.array(configGroupSchema).max(20).default([]),
    /** Whether a changed field takes effect only after a restart (fields can override). */
    restartRequired: z.boolean().default(true),
    /**
     * Keys something else rewrites (the egg's config parser on every start): shown read-only
     * and refused on save, so the form never fights hearthd.
     */
    protectedKeys: z.array(configKey).max(50).default([]),
  })
  .superRefine((e, ctx) => {
    if (e.groups.length && !(CONFIG_FORM_FORMATS as readonly string[]).includes(e.format))
      ctx.addIssue({
        code: 'custom',
        path: ['groups'],
        message: `${e.format} files are raw-only for now; leave groups empty.`,
      });
    const seen = new Set<string>();
    for (const f of configFields(e)) {
      if (seen.has(f.key))
        ctx.addIssue({ code: 'custom', path: ['groups'], message: `Key ${f.key} appears twice.` });
      seen.add(f.key);
    }
  });
export type ConfigEditor = z.infer<typeof configEditorSchema>;

/** Every field of an editor, in display order. */
export function configFields(editor: Pick<ConfigEditor, 'groups'>): ConfigField[] {
  return editor.groups.flatMap((g) => g.fields);
}

/** Whether an editor has a form (and not only the raw view). */
export function hasConfigForm(editor: Pick<ConfigEditor, 'format' | 'groups'>): boolean {
  return (
    editor.groups.length > 0 && (CONFIG_FORM_FORMATS as readonly string[]).includes(editor.format)
  );
}

/** Whether saving a change to this field needs a restart before the game sees it. */
export function configFieldNeedsRestart(
  editor: Pick<ConfigEditor, 'restartRequired'>,
  field: ConfigField,
): boolean {
  return field.restartRequired ?? editor.restartRequired;
}

/** A field's default as the string the file would hold, or undefined. */
export function configDefaultRaw(field: Pick<ConfigField, 'default'>): string | undefined {
  return field.default === undefined ? undefined : String(field.default);
}

const NUMBER = /^-?(?:\d+(?:\.\d+)?|\.\d+)$/;

/** Check text-like rules (length, pattern) on one string. */
function textRules(field: ConfigField, s: string, ctx: z.RefinementCtx, what = 'This') {
  if (field.minLength !== undefined && s.length < field.minLength)
    ctx.addIssue({
      code: 'custom',
      message:
        field.minLength === 1
          ? `${what} can’t be empty.`
          : `${what} needs at least ${field.minLength} characters.`,
    });
  if (field.maxLength !== undefined && s.length > field.maxLength)
    ctx.addIssue({
      code: 'custom',
      message: `${what} can be at most ${field.maxLength} characters.`,
    });
  if (field.pattern && !new RegExp(field.pattern, 'u').test(s))
    ctx.addIssue({ code: 'custom', message: field.patternMessage ?? `${what} isn’t valid.` });
}

/** Split a `list` field's raw value into trimmed items (an empty string is no items). */
export function splitConfigList(field: Pick<ConfigField, 'separator'>, raw: string): string[] {
  if (!raw.trim()) return [];
  return raw.split(field.separator ?? ',').map((s) => s.trim());
}

/**
 * The Zod schema for one field's raw value: the single place the declarative rules are
 * compiled, shared by the form (as you type) and the API (on save).
 */
export function configFieldValueSchema(field: ConfigField): z.ZodType<string> {
  return z
    .string()
    .max(65_536)
    .superRefine((raw, ctx) => {
      if (field.type !== 'multiline' && /[\r\n]/.test(raw)) {
        ctx.addIssue({ code: 'custom', message: 'This must be a single line.' });
        return;
      }
      switch (field.type) {
        case 'boolean':
          if (raw !== 'true' && raw !== 'false')
            ctx.addIssue({ code: 'custom', message: 'Must be true or false.' });
          return;
        case 'number': {
          if (!NUMBER.test(raw.trim())) {
            ctx.addIssue({ code: 'custom', message: 'Must be a number.' });
            return;
          }
          const n = Number(raw);
          if (field.integer !== false && !Number.isInteger(n))
            ctx.addIssue({ code: 'custom', message: 'Must be a whole number.' });
          if (field.min !== undefined && n < field.min)
            ctx.addIssue({ code: 'custom', message: `Must be at least ${field.min}.` });
          if (field.max !== undefined && n > field.max)
            ctx.addIssue({ code: 'custom', message: `Must be at most ${field.max}.` });
          return;
        }
        case 'select':
          if (!field.options?.some((o) => o.value === raw))
            ctx.addIssue({ code: 'custom', message: 'Pick one of the listed options.' });
          return;
        case 'list': {
          const items = splitConfigList(field, raw);
          if (items.some((i) => !i))
            ctx.addIssue({ code: 'custom', message: 'Remove the empty item.' });
          if (field.maxItems !== undefined && items.length > field.maxItems)
            ctx.addIssue({ code: 'custom', message: `At most ${field.maxItems} items.` });
          for (const item of items.filter(Boolean)) textRules(field, item, ctx, `“${item}”`);
          return;
        }
        default:
          textRules(field, raw, ctx);
      }
    });
}

// --- API shapes ------------------------------------------------------------------------

export const configEditorParamsSchema = z.object({
  server: z.string().regex(/^[0-9a-f]{8}$|^[0-9a-f-]{36}$/i),
  editor: editorId,
});

/** An editor's file as the form sees it. Secret values are never sent, only whether set. */
export const configStateSchema = z.object({
  editor: z.string(),
  path: z.string(),
  format: z.enum(CONFIG_FORMATS),
  exists: z.boolean(),
  /** Why the form can't be used (the file couldn't be parsed); Raw still works. */
  parseError: z.string().nullable(),
  /** Raw value of each known, non-secret field; null when the key isn't in the file. */
  values: z.record(z.string(), z.string().nullable()),
  /**
   * Keys the form shows read-only and the API refuses: the editor's own plus those the egg's
   * config parser rewrites on every start.
   */
  protectedKeys: z.array(z.string()),
  /** Secret fields that have a non-empty value in the file. */
  secretsSet: z.array(z.string()),
  /** Keys in the file the form doesn't show; they are kept as they are on save. */
  otherKeys: z.number().int(),
});
export type ConfigState = z.infer<typeof configStateSchema>;

export const configSaveBodySchema = z.object({
  /** Only the fields to change, as raw strings. */
  values: z
    .record(configKey, z.string().max(65_536))
    .refine((v) => Object.keys(v).length <= 500, 'Too many fields.'),
});
export type ConfigSaveBody = z.infer<typeof configSaveBodySchema>;

export const configSaveResultSchema = z.object({
  /** Keys whose value actually changed. */
  changed: z.array(z.string()),
  /** Some changed field only takes effect after a restart. */
  restartRequired: z.boolean(),
});
export type ConfigSaveResult = z.infer<typeof configSaveResultSchema>;
